import { ManagementOauthApi } from '../api/apis';
import { apiClient } from './apiClient';
import { Client } from '../types/client';

/**
 * Normalize client response: ensure both `name` and `clientName` are populated
 * for backwards compatibility during the name→clientName migration.
 */
function normalizeClient(raw: any): Client {
  const clientName = raw.clientName || raw.name || '';
  return {
    ...raw,
    clientName,
    name: clientName,
  };
}

class ClientService {
  private api: ManagementOauthApi;
  private clientsCache: Map<string, string> = new Map();
  private allClients: Client[] = [];
  private lastFetch: number = 0;
  // 5 min TTL — the client list only changes when an admin mutates a
  // client, and pages that care already call clearCache() on mutation.
  // The previous 1 min window caused DevicesTable + ClientsTable to
  // both refetch every page-open at production scale.
  private cacheDuration: number = 5 * 60 * 1000;
  // In-flight request dedup — if two components mount at the same time,
  // they share a single network round-trip.
  private inflight: Promise<Client[]> | null = null;
  // Per-deviceType cache of clients that own ≥1 device of that type.
  // Keyed by uppercase hardware-type code; each entry stores the list
  // and the fetch timestamp. Shared TTL with the unfiltered cache.
  private byTypeCache: Map<string, { clients: Client[]; at: number }> = new Map();
  private byTypeInflight: Map<string, Promise<Client[]>> = new Map();

  constructor() {
    this.api = new ManagementOauthApi(apiClient.getConfiguration());
  }

  async fetchAllClients(): Promise<Client[]> {
    const now = Date.now();
    if (this.allClients.length > 0 && now - this.lastFetch < this.cacheDuration) {
      return this.allClients;
    }
    if (this.inflight) {
      return this.inflight;
    }

    this.inflight = (async () => {
      try {
        const rawClients = await this.api.apiGetClientListV2V2ClientGet({}) as any[];
        const clients = (Array.isArray(rawClients) ? rawClients : []).map(normalizeClient);
        this.allClients = clients;
        this.lastFetch = Date.now();

        // Update cache
        this.clientsCache.clear();
        clients.forEach(client => {
          this.clientsCache.set(client.id, client.clientName);
        });

        return clients;
      } catch (error) {
        console.error('Failed to fetch clients:', error);
        return [];
      } finally {
        this.inflight = null;
      }
    })();
    return this.inflight;
  }

  /**
   * Variant of fetchAllClients that hides soft-deleted rows. Use this
   * for any picker or autocomplete; reserve fetchAllClients() for
   * surfaces that resolve a client name for an already-assigned ID
   * (where the row may legitimately be inactive).
   *
   * Strict equality on `=== true` so partial/undefined data (e.g. a
   * row missing the field in transit) is treated as inactive — the
   * safer default for any UI that's about to associate an entity
   * with the picked client.
   */
  async fetchActiveClients(): Promise<Client[]> {
    const all = await this.fetchAllClients();
    return all.filter((c) => c.active === true);
  }

  async getClientName(clientId: string): Promise<string> {
    if (this.clientsCache.has(clientId)) {
      return this.clientsCache.get(clientId)!;
    }

    await this.fetchAllClients();
    return this.clientsCache.get(clientId) || clientId;
  }

  /**
   * Fetch clients that own at least one device of the given hardware-type
   * code. Backed by a server-side EXISTS filter on /v2/client; cached in
   * memory per device-type code so repeated modal opens are free. Returns
   * [] if deviceType is empty.
   */
  async fetchClientsByDeviceType(deviceType: string): Promise<Client[]> {
    if (!deviceType) return [];
    const key = deviceType.toUpperCase();
    const now = Date.now();
    const cached = this.byTypeCache.get(key);
    if (cached && now - cached.at < this.cacheDuration) {
      return cached.clients;
    }
    const inflight = this.byTypeInflight.get(key);
    if (inflight) return inflight;

    const promise = (async () => {
      try {
        const axiosInstance = apiClient.getAxiosInstance();
        const response = await axiosInstance.get('/v2/client', {
          params: { has_device_type: key },
        });
        const raw = Array.isArray(response.data)
          ? response.data
          : response.data?.items ?? [];
        const clients = (raw as unknown[]).map((r: any) => normalizeClient(r));
        this.byTypeCache.set(key, { clients, at: Date.now() });
        return clients;
      } catch (error) {
        console.error(`Failed to fetch clients for device type ${key}:`, error);
        return [];
      } finally {
        this.byTypeInflight.delete(key);
      }
    })();
    this.byTypeInflight.set(key, promise);
    return promise;
  }

  clearCache(): void {
    this.clientsCache.clear();
    this.allClients = [];
    this.lastFetch = 0;
    this.byTypeCache.clear();
  }

  /**
   * Get the Keycloak group info for a client.
   * Returns the group ID needed to add users to this client's group.
   */
  async getClientGroup(clientId: string): Promise<{ id: string; name: string; path: string } | null> {
    try {
      const axiosInstance = apiClient.getAxiosInstance();
      const response = await axiosInstance.get(`/v2/clients/${clientId}/group`);
      return response.data;
    } catch (error) {
      console.error(`Failed to get client group for ${clientId}:`, error);
      return null;
    }
  }

  /**
   * Update a client. Used for the client-level access switch: `active: false`
   * cuts the whole organisation off, every one of its users at once, because
   * the API narrows each caller's scope to clients that are still active.
   */
  async updateClient(clientId: string, patch: Partial<Client>): Promise<Client> {
    const axiosInstance = apiClient.getAxiosInstance();
    const response = await axiosInstance.patch(`/v2/client/${clientId}`, patch);
    this.clearCache();
    return normalizeClient(response.data);
  }
}

export const clientService = new ClientService();
